import type { Edge, Node } from '@xyflow/react';
import {
  WORKFLOW_FLOW_NODE_HEIGHT,
  WORKFLOW_FLOW_NODE_WIDTH,
  WORKFLOW_FLOW_TRIGGER_HEIGHT,
  WORKFLOW_FLOW_TRIGGER_WIDTH,
} from './workflow-flow-layout.constants';
import {
  WORKFLOW_FLOW_NODE_TYPE,
  WORKFLOW_FLOW_TRIGGER_TYPE,
  type WorkflowFlowNodeData,
} from './components';
import { workflowGraphToFlow } from './workflow-graph-to-flow';
import { WORKFLOW_EDGE_TYPE } from './workflow-edge-style';
import type {
  NestedWorkflowLink,
  NestedWorkflowTree,
} from './nested-workflow-tree';
import { countNestedHierarchyRemovalImpact } from './nested-workflow-hierarchy-edit';
import { resolveForEachInteriorOwners } from './components/node/edit-strategies/for-each-child-node';

/** React Flow node type for the box around one workflow's own graph. */
export const NESTED_WORKFLOW_GROUP_NODE_TYPE = 'nestedWorkflowGroup' as const;

export type NestedWorkflowGroupPlaceholder =
  | 'loading'
  | 'missing'
  | 'cycle'
  | 'too-deep';

/**
 * A workflow card inside a group.
 *
 * Carries the workflow it belongs to and its id *in that workflow*, because the
 * flow-level id is namespaced by the group (one workflow can appear in the tree
 * more than once) and no edit can be routed from the namespaced form alone.
 */
export type NestedWorkflowCardNodeData = WorkflowFlowNodeData & {
  readonly ownerWorkflowId: string;
  readonly ownerNodeId: string;
  /**
   * Whether a "runs" edge leaves this card, so it has to draw the right-hand
   * port that edge names.
   *
   * Decided by the layout rather than read off the card's own node, because the
   * two can differ: a for-each whose loop body runs a workflow anchors the edge
   * without being a `run-workflow` node itself. A card that worked it out
   * locally would have disagreed with the edge and left it unanchored.
   */
  readonly runsNestedWorkflow: boolean;
  /**
   * What loses a connection if this card is removed. Computed here because the
   * card components only ever see one node, never the graph around it.
   */
  readonly removalImpact: {
    readonly upstreamCount: number;
    readonly downstreamCount: number;
  };
};

export type NestedWorkflowGroupNodeData = {
  readonly workflowId: string;
  readonly name: string | null;
  readonly depth: number;
  /** The workflow the canvas is showing; it has no "open" affordance. */
  readonly isRoot: boolean;
  /** Set when there is no graph to draw inside, with the reason why. */
  readonly placeholder: NestedWorkflowGroupPlaceholder | null;
  /** Steps in this workflow's own graph — the header's stand-in when collapsed. */
  readonly stepCount: number;
  /** Drawn as a header alone, with its own steps left out. */
  readonly isCollapsed: boolean;
  /** False where collapsing would hide nothing: a placeholder, or no steps. */
  readonly canCollapse: boolean;
};

/**
 * Whether the group drawn at `key` should render as a header alone.
 *
 * Keyed by group rather than by workflow: one workflow can sit at several places
 * in a tree, and collapsing it in one place should not fold it everywhere else.
 * Depth comes along so a caller can default on it without tracking keys it has
 * not seen yet — levels arrive one fetch at a time.
 */
export type IsGroupCollapsed = (key: string, depth: number) => boolean;

/** Room for the group's title bar above the first card. */
export const NESTED_GROUP_HEADER_HEIGHT = 52;

/**
 * Handle id for the port a "runs" edge leaves a `run-workflow` card from.
 *
 * A card's own ports are top (in) and bottom (out), and the bottom one already
 * carries the step's place in its workflow's sequence. A nesting edge leaving the
 * same port ran straight down the vertical flow before turning right, so it read
 * as though it came from whatever card sat *below* the one that runs the child.
 * Nesting leaves the right-hand edge instead, which is also the direction the
 * columns go.
 */
export const NESTED_RUNS_SOURCE_HANDLE_ID = 'nested-runs-out';

/** Breathing room between a group's border and the graph inside it. */
const NESTED_GROUP_PADDING = 28;

/** Horizontal gap between one nesting level and the next. */
const NESTED_COLUMN_GAP = 120;

/** Vertical gap between two workflows that sit at the same level. */
const NESTED_ROW_GAP = 72;

/** Floor for a group's inner area, so an empty workflow still reads as a box. */
const MIN_CONTENT_WIDTH = WORKFLOW_FLOW_NODE_WIDTH;
const MIN_CONTENT_HEIGHT = 96;

/** Inner height of a group standing in for a workflow it could not draw. */
const PLACEHOLDER_CONTENT_HEIGHT = 96;

/**
 * Separators for composed ids. Node ids are uuids and trigger ids are
 * `trigger:<uuid>`, so neither can contain either of these.
 *
 * Descending into a child group and scoping a node to its group must use
 * *different* markers: both compose a group key with the same `run-workflow`
 * node id, so one marker would give the child group and its calling card the
 * same id — and a "runs" edge from a node to itself.
 */
const SCOPE_SEPARATOR = '~';
const DESCEND_SEPARATOR = '>';

/** Id of an inner node/edge, namespaced by the group it was drawn for. */
function scopedId(groupKey: string, id: string): string {
  return `${groupKey}${SCOPE_SEPARATOR}${id}`;
}

/** Key for the group a given `run-workflow` node opens. */
function childGroupKey(groupKey: string, nodeId: string): string {
  return `${groupKey}${DESCEND_SEPARATOR}${nodeId}`;
}

function nodeSize(node: Node): { width: number; height: number } {
  if (node.type === WORKFLOW_FLOW_TRIGGER_TYPE) {
    return {
      width: WORKFLOW_FLOW_TRIGGER_WIDTH,
      height: WORKFLOW_FLOW_TRIGGER_HEIGHT,
    };
  }
  return { width: WORKFLOW_FLOW_NODE_WIDTH, height: WORKFLOW_FLOW_NODE_HEIGHT };
}

function boundsOf(nodes: readonly Node[]): {
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  if (nodes.length === 0) {
    return { minX: 0, minY: 0, width: 0, height: 0 };
  }
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const node of nodes) {
    const { width, height } = nodeSize(node);
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + width);
    maxY = Math.max(maxY, node.position.y + height);
  }
  return { minX, minY, width: maxX - minX, height: maxY - minY };
}

type PlacedLink = {
  /** Where the nesting edge starts: the anchoring card, or the group itself. */
  readonly fromKey: string;
  /** False when no card anchors the call and the edge falls back to the group. */
  readonly fromCard: boolean;
  /** The anchoring card's offset inside its group, for lining the child up with it. */
  readonly fromOffsetY: number;
  readonly group: PlacedGroup;
};

type PlacedGroup = {
  readonly key: string;
  readonly data: NestedWorkflowGroupNodeData;
  readonly width: number;
  readonly height: number;
  /** The workflow's own flow, ids scoped and positions relative to the group. */
  readonly innerNodes: Node[];
  readonly innerEdges: Edge[];
  readonly links: readonly PlacedLink[];
};

function placeholderGroup(params: {
  key: string;
  link: NestedWorkflowLink;
  depth: number;
  placeholder: NestedWorkflowGroupPlaceholder;
}): PlacedGroup {
  return {
    key: params.key,
    data: {
      workflowId: params.link.targetWorkflowId,
      name: params.link.name,
      depth: params.depth,
      isRoot: false,
      placeholder: params.placeholder,
      stepCount: 0,
      isCollapsed: false,
      canCollapse: false,
    },
    width: MIN_CONTENT_WIDTH + NESTED_GROUP_PADDING * 2,
    height:
      NESTED_GROUP_HEADER_HEIGHT +
      PLACEHOLDER_CONTENT_HEIGHT +
      NESTED_GROUP_PADDING,
    innerNodes: [],
    innerEdges: [],
    links: [],
  };
}

/**
 * Lays one workflow out exactly as its own canvas would, then re-homes the result
 * inside a group box.
 *
 * Reusing {@link workflowGraphToFlow} is the point: the cards, the edges, the
 * synthesized trigger links and the hidden for-each interiors are all whatever
 * the editor canvas draws, so a workflow looks the same here as it does when you
 * open it.
 */
function prepareGroup(params: {
  tree: NestedWorkflowTree;
  key: string;
  isRoot: boolean;
  isCollapsed: IsGroupCollapsed;
}): PlacedGroup {
  const { tree, key, isRoot, isCollapsed } = params;
  const flow = workflowGraphToFlow(tree.graph);
  const stepCount = flow.nodes.filter(
    (node) => node.type === WORKFLOW_FLOW_NODE_TYPE
  ).length;
  // Which card stands in for each step the canvas does not draw, so a call made
  // from inside a loop body can still be anchored to something on screen.
  const interiorOwners = resolveForEachInteriorOwners(
    tree.graph.nodes,
    tree.graph.edges
  );
  const drawnNodeIds = new Set(flow.nodes.map((node) => node.id));
  /**
   * The card a link's "runs" edge leaves from: the calling node itself when the
   * canvas draws it, otherwise the for-each that runs it as its loop body.
   *
   * Undefined only when neither is on the canvas, which the caller answers by
   * falling back to the group box.
   */
  const anchorNodeIdFor = (nodeId: string): string | undefined => {
    if (drawnNodeIds.has(nodeId)) {
      return nodeId;
    }
    const owner = interiorOwners.get(nodeId);
    return owner !== undefined && drawnNodeIds.has(owner) ? owner : undefined;
  };
  const anchorNodeIds = new Set(
    tree.links
      .map((link) => anchorNodeIdFor(link.nodeId))
      .filter((nodeId): nodeId is string => nodeId !== undefined)
  );
  // A workflow with no steps has nothing to fold away, so it is never collapsed
  // however the predicate answers — an empty box that also hides nothing would
  // give the header a chevron that appears to do nothing.
  const collapsed = stepCount > 0 && isCollapsed(key, tree.depth);
  const bounds = boundsOf(flow.nodes);
  const contentWidth = Math.max(bounds.width, MIN_CONTENT_WIDTH);
  const contentHeight = Math.max(bounds.height, MIN_CONTENT_HEIGHT);

  const innerNodes: Node[] = collapsed
    ? []
    : flow.nodes.map((node) => ({
        ...node,
        id: scopedId(key, node.id),
        parentId: key,
        draggable: false,
        selectable: false,
        position: {
          x: node.position.x - bounds.minX + NESTED_GROUP_PADDING,
          y: node.position.y - bounds.minY + NESTED_GROUP_HEADER_HEIGHT,
        },
        ...(node.type === WORKFLOW_FLOW_NODE_TYPE
          ? {
              data: {
                ...(node.data as WorkflowFlowNodeData),
                ownerWorkflowId: tree.workflowId,
                ownerNodeId: node.id,
                runsNestedWorkflow: anchorNodeIds.has(node.id),
                removalImpact: countNestedHierarchyRemovalImpact(
                  tree.graph,
                  node.id
                ),
              } satisfies NestedWorkflowCardNodeData,
            }
          : {}),
      }));

  const innerEdges: Edge[] = collapsed
    ? []
    : flow.edges.map((edge) => ({
        ...edge,
        id: scopedId(key, edge.id),
        source: scopedId(key, edge.source),
        target: scopedId(key, edge.target),
        selectable: false,
        reconnectable: false,
      }));

  const offsetYById = new Map(
    innerNodes.map((node) => [node.id, node.position.y] as const)
  );

  const links: PlacedLink[] = tree.links.map((link) => {
    // A `run-workflow` node inside a for-each's loop body has no card of its
    // own: the canvas folds the interior into the for-each, which carries the
    // nested workflow's name as a badge. The edge leaves that for-each, because
    // that is where the call visibly happens. Only when neither is drawn — a
    // collapsed group has no cards at all — does the child hang off the group
    // box, so the hierarchy stays connected either way.
    const anchorNodeId = anchorNodeIdFor(link.nodeId);
    const nodeKey =
      anchorNodeId === undefined ? undefined : scopedId(key, anchorNodeId);
    const fromOffsetY =
      nodeKey === undefined ? undefined : offsetYById.get(nodeKey);
    const childKey = childGroupKey(key, link.nodeId);
    return {
      fromKey:
        nodeKey !== undefined && fromOffsetY !== undefined ? nodeKey : key,
      fromCard: nodeKey !== undefined && fromOffsetY !== undefined,
      fromOffsetY: fromOffsetY ?? NESTED_GROUP_HEADER_HEIGHT,
      group:
        link.child.kind === 'workflow'
          ? prepareGroup({
              tree: link.child.tree,
              key: childKey,
              isRoot: false,
              isCollapsed,
            })
          : placeholderGroup({
              key: childKey,
              link,
              depth: tree.depth + 1,
              placeholder: link.child.kind,
            }),
    };
  });

  return {
    key,
    data: {
      workflowId: tree.workflowId,
      name: tree.name,
      depth: tree.depth,
      isRoot,
      placeholder: null,
      stepCount,
      isCollapsed: collapsed,
      canCollapse: stepCount > 0,
    },
    // Collapsed keeps the column's width but sheds its height: a workflow lays
    // out top-to-bottom, so its steps cost vertical space, and an eight-step box
    // is over a thousand pixels tall against the same 356 wide.
    width: collapsed
      ? MIN_CONTENT_WIDTH + NESTED_GROUP_PADDING * 2
      : contentWidth + NESTED_GROUP_PADDING * 2,
    height: collapsed
      ? NESTED_GROUP_HEADER_HEIGHT
      : contentHeight + NESTED_GROUP_HEADER_HEIGHT + NESTED_GROUP_PADDING,
    innerNodes,
    innerEdges,
    links,
  };
}

/** Widest group at each nesting level, which is what sets the column pitch. */
function columnWidths(root: PlacedGroup): number[] {
  const widths: number[] = [];
  const visit = (group: PlacedGroup, depth: number): void => {
    widths[depth] = Math.max(widths[depth] ?? 0, group.width);
    for (const link of group.links) {
      visit(link.group, depth + 1);
    }
  };
  visit(root, 0);
  return widths;
}

function nestingEdge(params: {
  fromKey: string;
  toKey: string;
  fromCard: boolean;
}): Edge {
  return {
    id: `nested${SCOPE_SEPARATOR}${params.fromKey}${SCOPE_SEPARATOR}${params.toKey}`,
    source: params.fromKey,
    target: params.toKey,
    // Cards get a dedicated right-hand port; the group fallback has only its own
    // single source handle, so naming one there would match nothing.
    ...(params.fromCard ? { sourceHandle: NESTED_RUNS_SOURCE_HANDLE_ID } : {}),
    type: WORKFLOW_EDGE_TYPE,
    label: 'runs',
    selectable: false,
    reconnectable: false,
    // Dashed, so a call into another workflow never reads as one more step in
    // this one's sequence.
    style: { strokeDasharray: '6 6' },
    labelStyle: { fill: 'var(--muted-foreground)', fontSize: 10 },
    labelBgStyle: { fill: 'var(--background)' },
    labelBgPadding: [2, 6],
    labelBgBorderRadius: 4,
  };
}

/**
 * Turns a nested workflow tree into a single React Flow graph.
 *
 * Two axes carry the two relationships. Each workflow keeps its own top-to-bottom
 * execution graph inside its box, untouched; nesting runs left to right, one
 * column per level, with a dashed "runs" edge from the calling node into the box
 * it opens. So the whole hierarchy is one picture, and no workflow has to be
 * redrawn to fit into it.
 *
 * A child is lined up with the node that runs it, and siblings stack down their
 * column, which keeps the "runs" edges short and mostly non-crossing.
 *
 * `isCollapsed` folds a workflow's own steps away while leaving it, and everything
 * it runs, in place — so the nesting stays whole and only the per-workflow detail
 * goes. Nothing is collapsed unless a caller says so: what to fold by default is a
 * product decision, and this pass only lays out what it is handed.
 */
export function nestedWorkflowTreeToFlow(
  tree: NestedWorkflowTree,
  options?: { isCollapsed?: IsGroupCollapsed }
): {
  nodes: Node[];
  edges: Edge[];
} {
  const isCollapsed = options?.isCollapsed ?? (() => false);
  const root = prepareGroup({
    tree,
    key: tree.workflowId,
    isRoot: true,
    isCollapsed,
  });
  const widths = columnWidths(root);

  const columnX: number[] = [];
  let x = 0;
  for (let depth = 0; depth < widths.length; depth += 1) {
    columnX[depth] = x;
    x += (widths[depth] ?? 0) + NESTED_COLUMN_GAP;
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  /** Next free y per column, so siblings stack instead of overlapping. */
  const cursors: number[] = [];

  const place = (group: PlacedGroup, depth: number, minTop: number): void => {
    const top = Math.max(cursors[depth] ?? 0, minTop, 0);
    // The group has to precede its children in the array: React Flow resolves
    // `parentId` against the nodes it has already seen.
    nodes.push({
      id: group.key,
      type: NESTED_WORKFLOW_GROUP_NODE_TYPE,
      position: { x: columnX[depth] ?? 0, y: top },
      data: group.data,
      draggable: false,
      selectable: false,
      style: { width: group.width, height: group.height },
    });
    nodes.push(...group.innerNodes);
    edges.push(...group.innerEdges);
    cursors[depth] = top + group.height + NESTED_ROW_GAP;

    for (const link of group.links) {
      // Emitted before descending, so edges come out outside-in and read in the
      // same order as the columns.
      edges.push(
        nestingEdge({
          fromKey: link.fromKey,
          toKey: link.group.key,
          fromCard: link.fromCard,
        })
      );
      place(
        link.group,
        depth + 1,
        top + link.fromOffsetY - NESTED_GROUP_HEADER_HEIGHT
      );
    }
  };

  place(root, 0, 0);
  return { nodes, edges };
}
